/**
 * PermanentDeleteOrgDialog — two-phase plan + execute for the
 * destructive `POST /api/platform/orgs/:orgId/permanent-delete` flow
 * (slice 3 — see `ai-docs/deleted-orgs-reinstate-handoff.md`).
 *
 * Mirrors the structural pattern of the runtime fleet's
 * `FleetUpgradeAllDialog`:
 *
 *   1. **PLAN** (on open) — POST with empty body, server returns
 *      `targets[]` + `confirmPhrase` ("PERMANENTLY DELETE <slug>") +
 *      `executionToken` (120s TTL). Read-only; no containers touched.
 *   2. **EXECUTE** — operator types the literal `confirmPhrase` back
 *      into a text input + provides a 3-500 char `reason`. Sends
 *      `{executionToken, confirm, reason}` to the same endpoint.
 *
 * Self-heals on the four "plan went stale" error codes by re-fetching
 * the plan inline: `INVALID_TOKEN` / `TOKEN_EXPIRED` / `SCOPE_MISMATCH`
 * (token tampered or expired or mismatched) / `SET_CHANGED` (the
 * tombstone/routing set drifted between plan and execute).
 */

import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import { PlatformApi, PlatformApiError } from "@/lib/platform-api";
import type { OrgRuntimesReport } from "@/types/platform";

const REASON_MIN = 3;
const REASON_MAX = 500;

// Server error codes that signal "plan stale, re-plan and try again"
// (vs. inline-fixable errors like CONFIRM_MISMATCH / VALIDATION_ERROR).
const STALE_PLAN_CODES = new Set([
  "INVALID_TOKEN",
  "TOKEN_EXPIRED",
  "SCOPE_MISMATCH",
  "SET_CHANGED",
]);

interface PlanData {
  orgId: string;
  slug: string;
  targetCount: number;
  targets: Array<{
    userId: string;
    containerName: string;
    deployTarget: string;
    source: "tombstone" | "routing";
  }>;
  confirmPhrase: string;
  executionToken: string;
}

export interface PermanentDeleteOrgDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Non-null while the dialog is open + active. */
  orgId: string | null;
  /** Called after a successful execute — parent refreshes the list + may toast. */
  onSuccess: (result: { slug: string; runtimes: OrgRuntimesReport<"deleted"> }) => void;
}

async function fetchPlan(
  api: PlatformApi,
  orgId: string,
): Promise<PlanData> {
  const res = await api.planPermanentDelete(orgId);
  return {
    orgId: res.data.orgId,
    slug: res.data.slug,
    targetCount: res.data.targetCount,
    targets: res.data.targets,
    confirmPhrase: res.data.confirmPhrase,
    executionToken: res.data.executionToken,
  };
}

export function PermanentDeleteOrgDialog({
  open,
  onOpenChange,
  orgId,
  onSuccess,
}: PermanentDeleteOrgDialogProps) {
  const api = usePlatformApi();

  const [plan, setPlan] = useState<PlanData | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);

  const [typedConfirm, setTypedConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [executing, setExecuting] = useState(false);
  const [executeError, setExecuteError] = useState<string | null>(null);
  const [staleNotice, setStaleNotice] = useState<string | null>(null);

  // Plan-fetch on (re)open. Resets every field too — a previous
  // attempt's typed text never leaks into a fresh run.
  useEffect(() => {
    if (!open || !orgId) {
      setPlan(null);
      setTypedConfirm("");
      setReason("");
      setExecuteError(null);
      setStaleNotice(null);
      return;
    }
    let cancelled = false;
    setPlanLoading(true);
    setPlanError(null);
    setStaleNotice(null);
    fetchPlan(api, orgId)
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch((e) => {
        if (!cancelled) {
          setPlanError(
            e instanceof Error ? e.message : "Failed to plan permanent-delete",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setPlanLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orgId, api]);

  const trimmedReason = reason.trim();
  const reasonOk =
    trimmedReason.length >= REASON_MIN && trimmedReason.length <= REASON_MAX;
  const phraseOk = !!plan && typedConfirm.trim() === plan.confirmPhrase;
  const canSubmit = !!plan && !executing && !planLoading && phraseOk && reasonOk;

  const handleExecute = useCallback(async () => {
    if (!plan || !orgId || !canSubmit) return;
    setExecuting(true);
    setExecuteError(null);
    setStaleNotice(null);
    try {
      const res = await api.executePermanentDelete(orgId, {
        executionToken: plan.executionToken,
        confirm: typedConfirm.trim(),
        reason: trimmedReason,
      });
      onSuccess({ slug: res.data.slug, runtimes: res.data.runtimes });
      onOpenChange(false);
    } catch (err) {
      const code = err instanceof PlatformApiError ? err.code : null;
      const msg = err instanceof Error ? err.message : "Permanent-delete failed";
      if (code && STALE_PLAN_CODES.has(code)) {
        // Re-plan inline and ask the operator to confirm again. The
        // typed phrase stays (slug doesn't change between plans), but
        // the token is refreshed.
        setStaleNotice(
          `${code}: the plan went stale — re-planning. Please confirm again to proceed.`,
        );
        try {
          const fresh = await fetchPlan(api, orgId);
          setPlan(fresh);
          setExecuteError(null);
        } catch (re) {
          setExecuteError(re instanceof Error ? re.message : "Re-plan failed");
        }
      } else {
        setExecuteError(`${code ?? "ERROR"}: ${msg}`);
      }
    } finally {
      setExecuting(false);
    }
  }, [api, plan, orgId, canSubmit, typedConfirm, trimmedReason, onSuccess, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl space-y-4"
        data-testid="deleted-orgs-permanent-dialog"
      >
        <DialogHeader className="items-center sm:text-center">
          <DialogTitle className="flex items-center justify-center gap-2 text-2xl text-foreground">
            <AlertTriangle className="h-6 w-6 text-critical" />
            Permanently delete organization
          </DialogTitle>
          <DialogDescription>
            {plan ? (
              <>
                This will permanently remove{" "}
                <strong className="font-mono text-foreground">{plan.slug}</strong>{" "}
                and tear down <strong>{plan.targetCount}</strong>{" "}
                {plan.targetCount === 1 ? "runtime" : "runtimes"}.{" "}
                <span className="font-semibold text-critical">
                  This cannot be undone.
                </span>
              </>
            ) : (
              "Loading plan…"
            )}
          </DialogDescription>
        </DialogHeader>

        {planLoading && (
          <div className="flex items-center gap-2 rounded-md border border-border bg-panel-2 p-3 text-sm text-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            Reading the plan from lexserver…
          </div>
        )}

        {planError && !planLoading && (
          <div
            className="rounded-md border border-critical/40 bg-critical/10 p-3 text-sm text-critical"
            data-testid="deleted-orgs-permanent-plan-error"
          >
            Could not load the plan: {planError}
          </div>
        )}

        {plan && (
          <>
            <div className="rounded-md border border-critical/40 bg-critical/10 p-3 text-sm text-critical">
              <p className="font-medium">
                Tombstones removed; routing erased; org marked TERMINATED.
              </p>
              <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-xs">
                <li>
                  Affects <strong>every runtime</strong> listed below.
                </li>
                <li>
                  Reinstate becomes <strong>impossible</strong> after this
                  action.
                </li>
                <li>
                  Audited as <code className="font-mono">org.permanent_delete</code>.
                </li>
              </ul>
            </div>

            <div>
              <p className="mb-1 text-xs font-medium text-muted">
                Targets ({plan.targetCount}):
              </p>
              <ul
                data-testid="deleted-orgs-permanent-targets"
                className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border bg-panel-2 p-2 text-xs"
              >
                {plan.targets.map((t) => (
                  <li
                    key={`${t.userId}:${t.containerName}`}
                    className="flex items-center justify-between gap-2 font-mono"
                  >
                    <span className="truncate text-foreground">{t.containerName}</span>
                    <span className="shrink-0 text-faint">
                      {t.deployTarget} · {t.source}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-1">
              <label
                htmlFor="deleted-orgs-permanent-confirm-input"
                className="text-xs font-medium text-muted"
              >
                Type{" "}
                <span className="font-mono font-semibold text-foreground">
                  {plan.confirmPhrase}
                </span>{" "}
                to enable the action:
              </label>
              <input
                id="deleted-orgs-permanent-confirm-input"
                type="text"
                autoComplete="off"
                value={typedConfirm}
                onChange={(e) => setTypedConfirm(e.target.value)}
                placeholder={plan.confirmPhrase}
                disabled={executing}
                data-testid="deleted-orgs-permanent-confirm-input"
                className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 text-sm text-foreground outline-none placeholder:text-faint focus:border-critical focus:ring-1 focus:ring-critical/30 disabled:opacity-60"
              />
            </div>

            <div className="space-y-1">
              <label
                htmlFor="deleted-orgs-permanent-reason"
                className="text-xs font-medium text-muted"
              >
                Reason (audited; {REASON_MIN}-{REASON_MAX} chars):
              </label>
              <textarea
                id="deleted-orgs-permanent-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Why is this org being permanently deleted?"
                disabled={executing}
                data-testid="deleted-orgs-permanent-reason"
                className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 text-sm text-foreground outline-none placeholder:text-faint focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:opacity-60"
              />
            </div>
          </>
        )}

        {staleNotice && (
          <div
            className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm text-warning"
            data-testid="deleted-orgs-permanent-notice"
          >
            {staleNotice}
          </div>
        )}
        {executeError && (
          <div
            className="rounded-md border border-critical/40 bg-critical/10 p-3 text-sm text-critical"
            data-testid="deleted-orgs-permanent-error"
          >
            {executeError}
          </div>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={executing}
            data-testid="deleted-orgs-permanent-cancel"
            className="rounded-md border border-border bg-panel-2 px-3 py-1.5 text-sm font-medium text-muted transition-colors hover:bg-accent hover:text-foreground disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => void handleExecute()}
            data-testid="deleted-orgs-permanent-confirm"
            className="inline-flex items-center gap-1 rounded-md bg-critical px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-critical/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {executing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            {executing ? "Permanently deleting…" : "Permanently delete"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
